import {
  SavedCommodityCreatedSourceEnum,
  type SavedCommodity,
} from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";

/**
 * What's wrong with a saved commodity, from the point of view of getting a quote.
 *
 * These two stop a commodity quoting at all, which is why they're the filters. Haisue's sync produced
 * 3,473 rows with 63 unquotable, and finding those 63 meant a hand-written query (ENG-6665).
 */
export type SavedCommodityProblem = "missingDimensions" | "missingSku";

/** A commodity can't be quoted without all three dimensions. */
export function isMissingDimensions(commodity: SavedCommodity): boolean {
  return [
    commodity.lengthInches,
    commodity.widthInches,
    commodity.heightInches,
  ].some((value) => value === undefined || value <= 0);
}

/**
 * No SKU means nothing can match this commodity to a product.
 *
 * Blank counts as missing: whitespace reads as ready-to-quote and never quotes — the same trap as
 * ENG-6642 on the matching side.
 */
export function isMissingSku(commodity: SavedCommodity): boolean {
  return (commodity.skuIdentifier ?? "").trim().length === 0;
}

/** Not exported: every caller is in this file, and knip fails the build on an unused export. */
function problemsFor(commodity: SavedCommodity): SavedCommodityProblem[] {
  const problems: SavedCommodityProblem[] = [];
  if (isMissingDimensions(commodity)) problems.push("missingDimensions");
  if (isMissingSku(commodity)) problems.push("missingSku");
  return problems;
}

/**
 * `| undefined` written out rather than left to `?`: `exactOptionalPropertyTypes` is on, and "no
 * filter" is a value these callers pass explicitly.
 */
export interface SavedCommodityFilter {
  /** Undefined means every source, including rows that predate the column. */
  source?: SavedCommodityCreatedSourceEnum | undefined;
  /** Undefined means no problem filter — show everything, including healthy rows. */
  problem?: SavedCommodityProblem | undefined;
}

/**
 * The commodities a source/problem filter selects.
 *
 * Named for what it selects on: `filterSavedCommodities` is already the line-item picker's, and one
 * name for two functions is how a reader imports the wrong one.
 *
 * Source and problem are ANDed, because that's the question being asked: "which of the ones Shopify
 * made can't be quoted". A row with no `createdSource` is only excluded when a source is chosen —
 * absent means "we don't know", not "created by hand", so it must not be swept into any named
 * bucket.
 */
export function selectSavedCommoditiesByProblem(
  commodities: SavedCommodity[],
  filter: SavedCommodityFilter,
): SavedCommodity[] {
  return commodities.filter((commodity) => {
    if (
      filter.source !== undefined &&
      commodity.createdSource !== filter.source
    ) {
      return false;
    }
    if (
      filter.problem !== undefined &&
      !problemsFor(commodity).includes(filter.problem)
    ) {
      return false;
    }
    return true;
  });
}

/**
 * How many rows each problem filter would show, for the counts beside the filter labels.
 *
 * Computed against the source filter but not the problem filter, so the numbers stay put while
 * someone clicks between problems rather than each one recounting the last selection.
 */
export function problemCounts(
  commodities: SavedCommodity[],
  source: SavedCommodityCreatedSourceEnum | undefined,
): Record<SavedCommodityProblem, number> {
  const scoped = selectSavedCommoditiesByProblem(commodities, { source });
  return {
    missingDimensions: scoped.filter(isMissingDimensions).length,
    missingSku: scoped.filter(isMissingSku).length,
  };
}
